import { accountTypes, InputType } from 'config'

export interface SubmenuItem {
  key: string
  isExternal?: boolean
  title?: string // Custom title override
  loanStatus?: Record<string, boolean>
  accountTypes?: Record<string, boolean>
}

export interface FeatureData {
  title: string
  url: string
  order: number
  value: boolean
  permissions: string[]
  loanStatus: string[]
  submenus?: (string | SubmenuItem)[]
}

export const subMenus = {
  loanChangeRequest: { title: 'Loan Change Request' },
  orderValuation: { title: 'Order Valuation' },
  documentGenie: { title: 'Document Genie' },
  servicingNotes: { title: 'Servicing Notes' },
  closingCalendar: { title: 'Closing Calendar' },
  nationFlood: { title: 'Flood' },
  dataVerifySSA: { title: 'DataVerify SSA' },
  dataVerify4506: { title: 'DataVerify 4506' },
  dataVerifyDrive: { title: 'DataVerify Drive' },
  lexis: { title: 'SmartLinx Person Report' },
  servicingInformation: { title: 'Servicing Information' },
  orderOuts: { title: 'Order Outs' },
  loanIntakeDocuments: { title: 'Loan Intake Documents' },
  loanDetailValidation: { title: 'Loan Detail Validation' },
  tridImportantDates: { title: 'TRID Important Dates' },
  richerValues: { title: 'Richer Values' },
  secondaryMarketing: { title: 'Secondary Marketing' },
  appraisalReview: { title: 'Valuation Review' },
  expirationDates: { title: 'Expiration Dates' },
  underwritingSummary: { title: 'Underwriting Summary' },
  appraisalInformation: { title: 'Appraisal Information' },
  nyTransactions: { title: 'NY Transactions' },
  escrowHoldbackAgreement: { title: 'Escrow Holdback Agreement' },
  documentGenerate: { title: 'Document Generate' },
  lightningDocs: { title: 'Lightning Docs' },
  automateLoanDocs: { title: 'Automate Loan Docs' },
  goDocs: { title: 'GoDocs' },
  docMagic: { title: 'DocMagic' },
  aggregateEscrow: { title: 'Aggregate Escrow' },
  hud1Page1: { title: 'HUD1 Page1' },
  hud1Page2: { title: 'HUD1 Page2' },
  hud1Page3: { title: 'HUD1 Page3' },
  hud1A: { title: 'HUD1A' },
  funding: { title: 'Funding' },
  general: { title: 'General' },
  allonge: { title: 'Allonge' },
  assignment: { title: 'Assignment' },
  transferLetter: { title: 'Transfer Letter' },
  servicingTransfer: { title: 'Servicing Transfer' },
  purchaseAdviceBuyer: { title: 'Purchase Advice Buyer' },
  purchaseAdviceSeller: { title: 'Purchase Advice Seller' },
  crossRiverAssignmentDocs: { title: 'Cross River Assignment Docs' },
  shippingInformation: { title: 'Shipping Information' },
  amortizationSchedule: { title: 'Amortization Schedule' },
  postClosingLoanTrade: { title: 'Post Closing / Loan Trade' },
  collateralReview: { title: 'Collateral Review' },
  setPoint: { title: 'SetPoint' },
  // Servicing menu items
  ach: { title: 'ACH' },
  trustLedger: { title: 'Trust Ledger' },
  servicingDocument: { title: 'Servicing Documents' },
  loanExtensions: { title: 'Loan Extensions' },
  participation: { title: 'Investors' },
  defaultTracking: { title: 'Default Tracking' },
  creditReporting: { title: 'Credit Reporting' },
  globalNotes: { title: 'Notes' },
  loanActions: { title: 'Actions' },
  tasks: { title: 'Tasks' },
  invoice: { title: 'Invoices' },
  stripeInvoices: { title: 'Stripe Invoices' },
  payoff: { title: 'Payoff' },
  reinstatement: { title: 'Reinstatement' },
  statements: { title: 'Statements' },
  rateChanges: { title: 'Rate & Payment Changes' },
  budget: { title: 'Budget & Scope of Work' },
  loanPayments: { title: 'Loan Payments' },
  loanCharges: { title: 'Loan Charges' },
  penalties: { title: 'Penalties' },
  contacts: { title: 'Contacts' },
  fci: { title: 'FCI' },
  fciDetail: { title: 'FCI Detail' },
  mers: { title: 'MERS #' },
  // Servicing submenus that previously existed only in the hard-coded Servicing menu.
  // Listed here so an admin can enable, rename and order them like any other submenu.
  collectionCalls: { title: 'Collections Calls' },
  textMessages: { title: 'Text Messages' },
  borrowerPortal: { title: 'Borrower Portal' },
  partyPayments: { title: 'Party Payments' },
  paymentVouchers: { title: 'Payment Vouchers' },
  vouchers: { title: 'Escrow Vouchers' },
  borrowerInformation: { title: 'Borrower Information' },
  propertyInformation: { title: 'Property Information' },
  propertyLogistics: { title: 'Property Logistics' },

  // Funding menu items
  disbursementInformation: { title: 'Disbursement Information' },

  contractorApplication: { title: 'Contractor Application' },
  reminders: { title: 'Reminders' },
  feeWorksheet: { title: 'Fee Worksheet' },
  hmda: { title: 'HMDA' },
  rentalLedger: { title: 'Rental Ledger' },
  reoNotes: { title: 'REO Notes' },
  propertyProfile: { title: 'Property Profile' },
  financialTracking: { title: 'Financial Tracking' },
  dispositionStrategy: { title: 'Disposition Strategy' },
  reoContacts: { title: 'REO Contacts' },
  propertyExpense: { title: 'Property Expense' },
  improvementLedger: { title: 'Improvement Ledger' },
  reoDocument: { title: 'REO Documents' },
  drawRequest: { title: 'Draw Request' },
  borrowingBase: { title: 'Borrowing Base' },
  creditDenial: { title: 'Statement of Credit Denial' },
  servicingReport: { title: 'Servicing Report' },
  taxForms: { title: 'Tax Forms' },
  loanEstimatePage1: { title: 'Loan Estimate Page1' },
  loanEstimatePage2: { title: 'Loan Estimate Page 2' },
  loanEstimatePage3: { title: 'Loan Estimate Page 3' },
  closingDisclosurePage1: { title: 'Closing Disclosure Page 1' },
  closingDisclosurePage2: { title: 'Closing Disclosure Page 2' },
  closingDisclosurePage3: { title: 'Closing Disclosure Page 3' },
  closingDisclosurePage4: { title: 'Closing Disclosure Page 4' },
  closingDisclosurePage5: { title: 'Closing Disclosure Page 5' },
  closingDisclosureAddendum: { title: 'Loan Estimate Addendum' },
  transmittalSummary1008: { title: '1008 Transmittal Summary' },
  loanStructure: { title: 'Loan Structure' },
  loanApplication: { title: 'Loan Application' },
  loanSubmission: { title: 'Loan Submission' },
  compliance: { title: 'Compliance' },
  parties: { title: 'Parties' },
  emailRestrictions: { title: 'Email Restrictions' },
  aiProject: { title: 'AI Project' },
}

export const defaultFeatureKeys = [
  'conditions',
  'loan_change_request',
  'order_valuation',
  'documents_genie',
  'notes',
  'loan_actions',
  'closing_calendar',
  'loan_intake',
  'secondary_marketing',
  'underwriting',
  'closing_screen',
  'post_closing',
  'servicing',
  'budget_and_scope_of_work',
  'funding',
  'amortization_schedule',
  'trust_ledger',
  'contractor_application',
  'invoice',
  'tasks',
  'reminders',
  'fee_worksheet',
  'fci',
  'insurance_quote',
  'hmda',
  'creditDenial',
  'reo',
  'drawRequest',
  'construction',
  'borrowingBase',
  'ai_project',
  'email_restrictions',
]

export const featureInputs = (loanGlobalStatus: Record<string, any>): Record<string, InputType> => {
  return {
    key: {
      inputType: 'Text',
      title: 'Key (unique identifier)',
      required: true,
    },
    title: {
      inputType: 'Text',
      title: 'Title',
      required: true,
    },
    url: {
      inputType: 'Text',
      title: 'URL (optional, defaults to key)',
    },
    loanStatus: {
      inputType: 'multiselect',
      title: 'Loan Status',
      options: loanGlobalStatus,
    },
    permissions: {
      inputType: 'multiselect',
      title: 'Allowed account types',
      options: accountTypes,
      sort: true,
    },
  }
}

export const pagePermissionSubmenus: Record<string, string[]> = {
  loan_change_request: ['loanChangeRequest'],
  order_valuation: ['orderValuation'],
  document_genie: ['documentGenie'],
  notes: ['globalNotes'],
  loan_actions: ['loanActions'],
  closing_calendar: ['closingCalendar'],
  loanInTake: [
    'nationFlood',
    'dataVerifySSA',
    'dataVerify4506',
    'dataVerifyDrive',
    'lexis',
    'globalNotes',
    'orderValuation',
    'orderOuts',
    'loanIntakeDocuments',
    'loanDetailValidation',
    'tridImportantDates',
    'richerValues',
  ],
  secondary_marketing_screen: ['secondaryMarketing'],
  underwriting: [
    'appraisalReview',
    'expirationDates',
    'underwritingSummary',
    'appraisalInformation',
    'dataVerifySSA',
    'dataVerify4506',
    'dataVerifyDrive',
    'lexis',
    'nyTransactions',
    'escrowHoldbackAgreement',
    'globalNotes',
  ],
  closing_screen: [
    'documentGenerate',
    'lightningDocs',
    'automateLoanDocs',
    'goDocs',
    'docMagic',
    'aggregateEscrow',
    'hud1Page1',
    'hud1Page2',
    'hud1Page3',
    'hud1A',
    'funding',
  ],
  fee_worksheet: ['feeWorksheet'],
  post_closing: [
    'allonge',
    'assignment',
    'transferLetter',
    'servicingTransfer',
    'purchaseAdviceBuyer',
    'purchaseAdviceSeller',
    'crossRiverAssignmentDocs',
    'shippingInformation',
    'amortizationSchedule',
    'postClosingLoanTrade',
    'collateralReview',
    'setPoint',
  ],
  servicing: [
    'servicingInformation',
    'ach',
    'trustLedger',
    'servicingDocument',
    'loanExtensions',
    'participation',
    'defaultTracking',
    'creditReporting',
    'servicingNotes',
    'globalNotes',
    'tasks',
    'invoice',
    'orderValuation',
    'payoff',
    'reinstatement',
    'statements',
    'rateChanges',
    'budget',
    'loanPayments',
    'loanCharges',
    'penalties',
    'contacts',
    'fci',
    'mers',
    'servicingReport',
    'taxForms',
    'parties',
    'borrowerInformation',
    'propertyInformation',
    'reminders',
  ],
  budget: ['budget'],
  funding: ['disbursementInformation'],
  amortization_schedule: ['amortizationSchedule'],
  // Same screen as the servicing entry; a loan in origination reaches it here.
  trust_ledger: ['trustLedger'],
  contractor_application: ['contractorApplication'],
  invoice: ['stripeInvoices'],
  fci: ['fciDetail'],
  realEstateOwned: [
    'rentalLedger',
    'reoNotes',
    'propertyProfile',
    'financialTracking',
    'dispositionStrategy',
    'reoContacts',
    'propertyExpense',
    'improvementLedger',
    'reoDocument',
  ],
  stateOfCreditDenial: ['creditDenial'],
  borrowingBase: ['borrowingBase'],
  drawRequest: ['drawRequest'],
  construction: ['budget', 'drawRequest', 'globalNotes', 'loanExtensions', 'trustLedger'],
  hmda: ['hmda'],
  loan_ai_project: ['aiProject'],
  email_restrictions: ['emailRestrictions'],
  custom: [
    'loanStructure',
    'loanApplication',
    'loanSubmission',
    'propertyLogistics',
    'loanEstimatePage1',
    'loanEstimatePage2',
    'loanEstimatePage3',
    'closingDisclosurePage1',
    'closingDisclosurePage2',
    'closingDisclosurePage3',
    'closingDisclosurePage4',
    'closingDisclosurePage5',
    'closingDisclosureAddendum',
    'transmittalSummary1008',
    'compliance',
    'tridImportantDates',
    'parties',
  ],
}

export const submMenuMap: Record<string, string> = {
  nationalFlood: 'nationFlood',
  dataverifySSA: 'dataVerifySSA',
  dataverify4506: 'dataVerify4506',
  dataverifyDrive: 'dataVerifyDrive',
  lexis: 'lexis',
  appraisalReview: 'appraisalReview',
  expirationDates: 'expirationDates',
  underWritingSummary: 'underwritingSummary',
  appraisalInformation: 'appraisalInformation',
  nyTransactions: 'nyTransactions',
  escrowHoldbackAgreement: 'escrowHoldbackAgreement',
  docGen: 'documentGenerate',
  escrow: 'aggregateEscrow',
  hud1: 'hud1',
  hud1Page1: 'hud1Page1',
  hud1Page2: 'hud1Page2',
  hud1Page3: 'hud1Page3',
  hud1A: 'hud1A',
  funding: 'funding',
  allonge: 'allonge',
  assignment: 'assignment',
  transferLetter: 'transferLetter',
  servicingTransfer: 'servicingTransfer',
  purchaseAdviceBuyer: 'purchaseAdviceBuyer',
  purchaseAdviceSeller: 'purchaseAdviceSeller',
  download: 'crossRiverAssignmentDocs',
  shipping: 'shippingInformation',
  amortization: 'amortizationSchedule',
  trustLedger: 'trustLedger',
  servicingDocument: 'servicingDocument',
  loanExtensions: 'loanExtensions',
  servicingNotes: 'servicingNotes',
  participation: 'participation',
  defaultTracking: 'defaultTracking',
  creditReporting: 'creditReporting',
  invoice: 'invoice',
  orderValuation: 'orderValuation',
  payoff: 'payoff',
  reinstatement: 'reinstatement',
  statements: 'statements',
  rateChanges: 'rateChanges',
  budget: 'budget',
  loanPayments: 'loanPayments',
  fci: 'fci',
  disbursement: 'disbursementInformation',
  compliance: 'compliance',
  parties: 'parties',
}
